import { CurrencyPipe, DatePipe } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import { Component } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SessionService, WindowService } from '../../core';
import { ChangesDisplayService } from '../changes/changes-display.service';
import { IChangedFieldDetail } from '../pay-categories/pay-categories.type';
import { PayScalesService } from './pay-scales.service';
import {
	ITripRigReportRow,
	IPayScaleReportRow,
	IPayScale,
	IPayScaleHistory,
	IPilot,
	IYearOfServiceRecords,
	IPayScaleReport,
	ICalculatedPayScaleRates
} from './pay-scales.types';
import { PilotsService } from '../../shared/services/pilots.service';
import * as moment from 'moment';

/**
 * PayScalesComponent
 */
@Component({
	selector: 'app-pilotpay-pay-scales',
	templateUrl: './pay-scales.component.html'
})
export class PayScalesComponent {
	constructor(
		private changeDisplayService: ChangesDisplayService,
		private currencyPipe: CurrencyPipe,
		private datePipe: DatePipe,
		private sessionService: SessionService,
		private payScalesService: PayScalesService,
		private pilotsService: PilotsService,
		private route: ActivatedRoute,
		private router: Router
	) {
		this.isAdmin = this.sessionService.getUser().isAdmin;
		this.id = this.route.snapshot.paramMap.get('id');

		if (this.id) {
			this.payScaleId = parseInt(this.id, 10);
		}

		this.payScale = {
			yearOfServiceRecords: []
		};

		if (this.payScaleId) {
			this.getPayScale();

			// If logged in user is an admin, get a list of all pilots associated with this pay scale.
			// If logged in user is not an admin, only their pilot record should display in the pilots table
			this.sessionService.getUser().isAdmin
				? this.getPilots()
				: this.getPilot(this.sessionService.getUser().employeeId, moment.utc().format());
		}
	}

	public isAdmin: boolean = false;
	public id: string = '';
	public payScale: IPayScale;
	public pilots: IPilot[] = [];
	public selectedPayScaleId: number = 0;
	public payScaleId: number;
	public futureChangesToggle: boolean = false;
	public historyToggle: boolean = false;
	public payScaleHistory: IChangedFieldDetail[];
	public payScaleChanges: IChangedFieldDetail[];
	public isAddRecord: boolean = false;
	public yearsOfService: number = 0;
	public numberOfTrips: number;

	/**
	 * onYearOfServiceRecordsChange
	 */
	public onYearOfServiceRecordsChange(yearOfServiceRecords: IYearOfServiceRecords[]): void {
		if (yearOfServiceRecords) {
			this.payScale.yearOfServiceRecords = [...[], ...yearOfServiceRecords];
		}
	}

	/**
	 * getPayScale
	 */
	public getPayScale(): void {
		this.payScalesService.getPayScale(this.payScaleId).subscribe(
			(DATA: IPayScale[]) => {
				if (DATA && DATA.length) {
					this.payScale = DATA[0];
					if (!this.payScale.yearOfServiceRecords) {
						this.payScale.yearOfServiceRecords = [];
					}
				}

				if (this.sessionService.getUser().isAdmin) {
					this.getPayScaleFutureChanges();
				}

				this.getPayScaleHistory();
			},
			(error: HttpErrorResponse) => {
				console.error(error);
			}
		);
	}

	/**
	 * getPilots
	 */
	public getPilots(): void {
		this.pilotsService.getPilotsByPayScale(this.payScaleId).subscribe(
			(DATA: IPilot[]) => {
				if (DATA) {
					this.pilots = DATA;
				}
			},
			(error: HttpErrorResponse) => {
				console.error(error);
			}
		);
	}

	public navigate = (): void => {
		this.router
			.navigate(['/pilotpay/pay-scales', this.id])
			.then(() => {
				if (this.payScale && this.payScale.payScaleId) {
					this.selectedPayScaleId = this.payScale.payScaleId;
				}
			})
			.catch((err: Error) => {
				console.error(err);
			});
	};

	/**
	 * getPayScaleHistory
	 *
	 */
	public getPayScaleHistory(): void {
		this.payScaleHistory = [];
		this.payScalesService.getHistory(this.payScaleId).subscribe(
			(DATA: IPayScaleHistory[]) => {
				if (DATA) {
					const history: IChangedFieldDetail[] = this.changeDisplayService.convertPayScaleHistoryToChangedFieldDetail(
						DATA
					);
					this.payScaleHistory = [...this.payScaleHistory, ...history];
				}
			},
			(error: HttpErrorResponse) => {
				console.error(error);
			}
		);
	}

	/**
	 * getPayScaleFutureChanges
	 *
	 */
	public getPayScaleFutureChanges(): void {
		this.payScaleChanges = [];
		this.payScalesService.getScheduledChanges(this.payScaleId).subscribe(
			(DATA: IPayScaleHistory[]) => {
				if (DATA) {
					const history: IChangedFieldDetail[] = this.changeDisplayService.convertPayScaleHistoryToChangedFieldDetail(
						DATA
					);
					this.payScaleChanges = [...this.payScaleChanges, ...history];
				}
			},
			(error: HttpErrorResponse) => {
				console.error(error);
			}
		);
	}

	public onEstimatedNumberOfTrips(numberOfTrips: number): void {
		this.numberOfTrips = numberOfTrips;
	}

	/**
	 * Generate all the data necessary for printing the pay scale and trip pay rates
	 * based on an estimated number of trips
	 */
	public print(): void {
		const { aircraftModel, jobTitle } = this.payScale;
		const effectiveDate: string = this.datePipe.transform(this.payScale.effectiveStart, 'MMMM yyyy');
		const missionPay: string = this.currencyPipe.transform(this.payScale.perTripPay);
		const tripValue: string = this.currencyPipe.transform(this.numberOfTrips * this.payScale.perTripPay);

		const calculatedPayScaleRates: ICalculatedPayScaleRates[] = this.payScalesService.calculatePayScaleRates(
			this.numberOfTrips,
			this.payScale
		);

		const payScaleReportRows: IPayScaleReportRow[] = calculatedPayScaleRates.map(p => {
			const result: IPayScaleReportRow = [
				p.yearsOfService,
				p.guaranteedHours,
				this.currencyPipe.transform(p.hourlyRateDollars),
				this.currencyPipe.transform(p.biWeeklyRate),
				this.currencyPipe.transform(p.biWeeklyInstructorRate),
				this.currencyPipe.transform(p.monthlyRate),
				this.currencyPipe.transform(p.annualRate)
			];

			if (this.payScale.isApRatingPayScale) {
				result.push(this.currencyPipe.transform(p.apBiWeeklyRate), this.currencyPipe.transform(p.apAnnualRate));
			}

			return result;
		});

		const tripRigReportRows: ITripRigReportRow[] = calculatedPayScaleRates.map(p => {
			const result: ITripRigReportRow = [this.currencyPipe.transform(p.tripRigRate)];

			if (this.payScale.isApRatingPayScale) {
				result.push(this.currencyPipe.transform(p.apTripRigRate));
			}

			return result;
		});

		const payScaleReport: IPayScaleReport = {
			isApRatingScale: this.payScale.isApRatingPayScale,
			aircraftModel,
			effectiveDate,
			jobTitle,
			missionPay,
			payScaleReportRows,
			numberOfTrips: this.numberOfTrips,
			tripValue,
			tripRigReportRows
		};
		localStorage.setItem('payScaleReport', JSON.stringify(payScaleReport));
		WindowService.open('/assets/reports/pay-scale.html', 'Pay Scale Report');
	}

	public getPilot(employeeId: number, date: string): void {
		this.pilotsService.getPilot(employeeId, true, date).subscribe(
			(DATA: IPilot) => {
				this.pilots = [DATA];
				if (DATA.payScale) {
					this.yearsOfService = DATA.payScale.yearsOfService;
				}
			},
			(error: HttpErrorResponse) => {
				console.error(error);
			}
		);
	}
}
